import React, { useState, useEffect } from 'react';
import {
  RiAddLine, RiEditLine, RiDeleteBinLine, RiSearchLine, RiCloseLine,
  RiTimeLine, RiListCheck, RiPlayListAddLine,
} from 'react-icons/ri';
import { C, LEVELS } from '../../data/constants';
import {
  LevelBadge, ConfirmModal, PageHeader, FilterRow, FormGroup, EmptyState, Toast,
} from '../../components/shared/UI';
import {
  adminGetPracticeTests,
  adminCreatePracticeTest,
  adminUpdatePracticeTest,
  adminDeletePracticeTest,
} from '../../api/practiceTests.api';
import './PracticeTestManager.css';

const PracticeTestManager = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [filter, setFilter] = useState('N5');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [delId, setDelId] = useState(null);
  const [formData, setFormData] = useState(getEmptyFormData());
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  useEffect(() => {
    loadTests();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  function getEmptyFormData() {
    return {
      level: 'N5',
      title: '',
      description: '',
      version: '1.0',
      sections: [
        {
          name: 'vocabulary',
          displayName: 'Vocabulary',
          duration: 20,
          questions: [],
          audioUrl: '',
        },
        {
          name: 'grammar-reading',
          displayName: 'Grammar & Reading',
          duration: 20,
          questions: [],
          audioUrl: '',
        },
        {
          name: 'listening',
          displayName: 'Listening',
          duration: 20,
          questions: [],
          audioUrl: '',
        },
      ],
    };
  }

  const loadTests = async () => {
    try {
      setLoading(true);
      const response = await adminGetPracticeTests({ level: filter });
      setTests(response.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (test = null) => {
    if (test) {
      setEditingTest(test);
      setFormData({ ...test });
    } else {
      setEditingTest(null);
      setFormData(getEmptyFormData());
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingTest(null);
    setFormData(getEmptyFormData());
  };

  const handleSaveTest = async () => {
    if (!formData.title) return showToast('Title is required');
    try {
      if (editingTest) {
        const res = await adminUpdatePracticeTest(editingTest._id, formData);
        setTests(prev => prev.map(t => t._id === editingTest._id ? res.data : t));
        showToast('Test updated!');
      } else {
        const res = await adminCreatePracticeTest(formData);
        setTests(prev => [res.data, ...prev]);
        showToast('Test created!');
      }
      handleCloseForm();
    } catch (err) {
      showToast(err.message);
    }
  };

  const handleDeleteTest = async () => {
    try {
      await adminDeletePracticeTest(delId);
      setTests(prev => prev.filter(t => t._id !== delId));
      showToast('Test deleted.');
    } catch (err) {
      showToast(err.message);
    } finally {
      setDelId(null);
    }
  };

  const handleAddQuestion = (sectionIndex) => {
    const newSections = [...formData.sections];
    newSections[sectionIndex].questions.push({
      question: '',
      imageUrl: '',
      options: ['', '', '', ''],
      answer: 0,
      explanation: '',
    });
    setFormData({ ...formData, sections: newSections });
  };

  const handleUpdateQuestion = (sectionIndex, questionIndex, field, value) => {
    const newSections = [...formData.sections];
    newSections[sectionIndex].questions[questionIndex][field] = value;
    setFormData({ ...formData, sections: newSections });
  };

  const handleRemoveQuestion = (sectionIndex, questionIndex) => {
    const newSections = [...formData.sections];
    newSections[sectionIndex].questions.splice(questionIndex, 1);
    setFormData({ ...formData, sections: newSections });
  };

  const filteredTests = tests.filter(t => 
    t.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ padding: 32 }} className="anim-fade-up">
      <PageHeader 
        title="JLPT Practice Tests" 
        sub={`${tests.length} tests available`} 
        action={<button className="btn-primary" onClick={() => handleOpenForm()}><RiAddLine />Create Test</button>} 
      />

      <FilterRow>
        <div className="search-wrap" style={{ flex: 1, minWidth: 200 }}>
          <RiSearchLine size={15} />
          <input 
            className="ks-input" 
            placeholder="Search test title..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
          />
        </div>
        <select 
          className="ks-select" 
          style={{ width: 110 }} 
          value={filter} 
          onChange={e => setFilter(e.target.value)}
        >
          {LEVELS.map(l => <option key={l}>{l}</option>)}
        </select>
        <span style={{ fontSize: 13, color: C.muted }}>{filteredTests.length} results</span>
      </FilterRow>

      <div className="ks-card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
        ) : filteredTests.length === 0 ? (
          <EmptyState icon={RiSearchLine} title="No tests found" sub="Try a different search or add a new test." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="ks-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Level</th>
                  <th>Duration</th>
                  <th>Questions</th>
                  <th>Version</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTests.map(test => {
                  const totalQuestions = test.sections?.reduce(
                    (sum, s) => sum + (s.questions?.length || 0),
                    0
                  ) || 0;

                  return (
                    <tr key={test._id}>
                      <td style={{ fontWeight: 800 }}>{test.title}</td>
                      <td><LevelBadge level={test.level} /></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <RiTimeLine size={14} color={C.muted} />
                          {test.totalDuration} min
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <RiListCheck size={14} color={C.muted} />
                          {totalQuestions} Qs
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: C.muted }}>v{test.version}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-icon" onClick={() => handleOpenForm(test)}><RiEditLine size={14} /></button>
                          <button className="btn-icon danger" onClick={() => setDelId(test._id)}><RiDeleteBinLine size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={handleCloseForm}>
          <div className="modal-box anim-pop" style={{ maxWidth: 800, width: '90%', maxHeight: '90vh', overflowY: 'auto', padding: 28 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
              <h2 style={{ fontSize: 18, fontWeight: 900 }}>{editingTest ? 'Edit Practice Test' : 'Create Practice Test'}</h2>
              <button className="btn-icon" onClick={handleCloseForm}><RiCloseLine size={16} /></button>
            </div>

            <div className="ks-grid-2" style={{ gap: 16, marginBottom: 20 }}>
              <FormGroup label="Test Title">
                <input 
                  className="ks-input" 
                  value={formData.title} 
                  onChange={e => setFormData({ ...formData, title: e.target.value })} 
                  placeholder="e.g. N5 Mock Exam 1"
                />
              </FormGroup>
              <FormGroup label="Level">
                <select 
                  className="ks-select" 
                  value={formData.level} 
                  onChange={e => setFormData({ ...formData, level: e.target.value })}
                >
                  {LEVELS.map(l => <option key={l}>{l}</option>)}
                </select>
              </FormGroup>
            </div>

            <FormGroup label="Description">
              <textarea 
                className="ks-input" 
                rows="2" 
                value={formData.description} 
                onChange={e => setFormData({ ...formData, description: e.target.value })} 
                placeholder="Optional description..."
              />
            </FormGroup>

            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 14, fontWeight: 800, color: C.accent, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Sections</h3>
              
              {formData.sections.map((section, sIdx) => (
                <div key={section.name} style={{ background: C.bg, borderRadius: 12, padding: 20, marginBottom: 16, border: `1px solid ${C.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{section.displayName}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, color: C.muted }}>Duration (min):</span>
                      <input 
                        type="number" 
                        className="ks-input" 
                        style={{ width: 70, padding: '4px 8px' }} 
                        value={section.duration} 
                        onChange={e => {
                          const newSections = [...formData.sections];
                          newSections[sIdx].duration = parseInt(e.target.value) || 0;
                          setFormData({ ...formData, sections: newSections });
                        }}
                      />
                    </div>
                  </div>

                  {section.name === 'listening' && (
                    <FormGroup label="Audio URL">
                      <input 
                        className="ks-input" 
                        value={section.audioUrl} 
                        onChange={e => {
                          const newSections = [...formData.sections];
                          newSections[sIdx].audioUrl = e.target.value;
                          setFormData({ ...formData, sections: newSections });
                        }}
                        placeholder="URL to audio file..."
                      />
                    </FormGroup>
                  )}

                  <div style={{ background: C.card, borderRadius: 8, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>{section.questions.length} Questions</span>
                      <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => handleAddQuestion(sIdx)}>
                        <RiPlayListAddLine size={14} /> Add Question
                      </button>
                    </div>

                    {section.questions.map((q, qIdx) => (
                      <div key={qIdx} style={{ background: C.bg, borderRadius: 8, padding: 16, marginBottom: 12, border: `1px solid ${C.border}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                          <span style={{ fontSize: 11, fontWeight: 800, color: C.accent }}>QUESTION {qIdx + 1}</span>
                          <button className="btn-icon danger" onClick={() => handleRemoveQuestion(sIdx, qIdx)}><RiCloseLine size={14} /></button>
                        </div>

                        <textarea 
                          className="ks-input" 
                          rows="2" 
                          style={{ marginBottom: 10 }}
                          value={q.question} 
                          onChange={e => handleUpdateQuestion(sIdx, qIdx, 'question', e.target.value)} 
                          placeholder="Question text..."
                        />

                        {section.name !== 'listening' && (
                          <input 
                            className="ks-input" 
                            style={{ marginBottom: 10, fontSize: 12 }}
                            value={q.imageUrl} 
                            onChange={e => handleUpdateQuestion(sIdx, qIdx, 'imageUrl', e.target.value)} 
                            placeholder="Image URL (optional)..."
                          />
                        )}

                        <div className="ks-grid-2" style={{ gap: 8, marginBottom: 10 }}>
                          {q.options.map((opt, oIdx) => (
                            <div key={oIdx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input 
                                type="radio" 
                                name={`correct_${sIdx}_${qIdx}`} 
                                checked={q.answer === oIdx}
                                onChange={() => handleUpdateQuestion(sIdx, qIdx, 'answer', oIdx)}
                              />
                              <input 
                                className="ks-input" 
                                style={{ fontSize: 12, padding: '6px 10px' }}
                                value={opt} 
                                onChange={e => {
                                  const newOpts = [...q.options];
                                  newOpts[oIdx] = e.target.value;
                                  handleUpdateQuestion(sIdx, qIdx, 'options', newOpts);
                                }}
                                placeholder={`Option ${String.fromCharCode(65 + oIdx)}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={handleCloseForm}>Cancel</button>
              <button className="btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={handleSaveTest}>
                {editingTest ? 'Update Test' : 'Create Test'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {delId && (
        <ConfirmModal 
          message="Are you sure you want to delete this practice test? This will also remove all user attempts and progress associated with it."
          onConfirm={handleDeleteTest}
          onCancel={() => setDelId(null)}
        />
      )}

      <Toast message={toast} />
    </div>
  );
};

export default PracticeTestManager;
